import { useMemo } from 'react';
import { useSettingsContext } from './useSettings';
import { useSettingsList } from './useSettingsList';

/**
 * useResourceCategories — the shared derivation of "which resource
 * categories exist" for this tenant.
 *
 * ── Why this hook exists (dispatch 412 §1) ────────────────────────────
 *
 * Categories are NOT a settings list of their own. They are derived from
 * the `resource_types` items collection: built-in keys UNION the distinct
 * `.category` values across the tenant's existing resource rows. A custom
 * category exists only while at least one resource carries it.
 *
 * The vocab seed `resource_category.v1` (vocabSeeds:318) is a LABEL
 * vocabulary, not a writer. Reading options from it would give the WO
 * create sheet's category chooser a DIFFERENT list from what Settings →
 * Resource Types shows — inverting the stakeholder's ask
 * (UAT 08-19 :69, "our categories that are in there").
 *
 * Prior to this hook, the derivation lived inline in ResourceTypesPage.js.
 * D-07 introduces a second consumer (ResourceForm's custom-category
 * branch, called from the WO create sheet). This hook is the shared
 * home; ResourceTypesPage now consumes it as well. If a third caller
 * appears, it consumes this — never re-derives.
 *
 * ── Cascade contract (dispatch 412 §2) ────────────────────────────────
 *
 *   LOADING     — items in flight; `categories` still returns built-ins
 *                 synchronously (never empty during load).
 *   POPULATED   — built-ins ∪ distinct customs from items.
 *   ERROR       — built-ins only; `error` non-null so consumers can
 *                 disclose the partial state (Family-C shape). Silent
 *                 error-swallowing here would strand callers with an
 *                 incomplete list rendered as complete.
 *
 * ── Dormant plurality hazard (dispatch 413a §1) ────────────────────────
 *
 * `resource_category` is NOT currently populated into the useSettings
 * cache (SETTINGS_LISTS carries `resource_types`, not `resource_category`).
 * getEnumLabel therefore falls through step 1 (cache) to step 2 (seed),
 * whose labels are byte-identical to the historical hardcoded values on
 * ResourceTypesPage. If a future caller wires `resource_category` into
 * the settings cache (either directly or via a new SET_ID_ALIASES entry),
 * a tenant row carrying, e.g., "Vehicle" singular would silently change
 * both the WO chooser AND every Settings-page heading that reads via
 * getEnumLabel. Do not wire that list without a migration for the
 * plurality mismatch flagged in vocabSeeds:327.
 *   EMPTY       — UNREACHABLE. Built-ins are unconditional.
 *   NOT PERMITTED — no such gate at source (dispatch 412 §4, verified).
 *
 * ── Return ────────────────────────────────────────────────────────────
 *
 *   categories : Array<{ key, label, isCustom }>
 *   loading    : boolean  (first-fetch in flight)
 *   error      : any      (non-null on failed items fetch)
 *   refetch    : () => Promise<void>  (bypasses the module cache)
 */

// Module constant. Duplicated in ResourceTypesPage.js only until that
// page migrates to import from here (same commit — enforced by the
// migration step, not runtime). Do not change without updating both.
export const BUILT_IN_CATEGORIES = ['equipment', 'vehicles', 'tools'];

export function useResourceCategories() {
  const { items, loading, error, refetch } = useSettingsList('resource_types');
  const { getEnumLabel } = useSettingsContext();

  const categories = useMemo(() => {
    // Built-ins first, always. Even during LOADING and ERROR.
    const builtIn = BUILT_IN_CATEGORIES.map((key) => ({
      key,
      // Seed labels via getEnumLabel; fall back to word-case if the seed
      // ever loses an entry (defensive — no known caller currently).
      // Labels resolve via useSettings.getEnumLabel → prettifyEnum on
      // step 4 (see useSettings:290). For built-in keys the seed labels
      // supply readable strings; for any input, prettifyEnum returns a
      // non-empty string (empty only for separator-only strings, which
      // this codebase never feeds it because the custom-input flow
      // trims and rejects blank).
      label: getEnumLabel('resource_category', key),
      isCustom: false,
    }));

    // Customs: distinct .category values on active resource rows that are
    // not already built-ins. Empty/nullish categories are ignored so the
    // dropdown never grows a ghost blank row.
    const seen = new Set(BUILT_IN_CATEGORIES);
    const customs = [];
    for (const row of items || []) {
      const key = row && row.category;
      if (!key || typeof key !== 'string') continue;
      if (seen.has(key)) continue;
      seen.add(key);
      customs.push({
        key,
        // Custom keys resolve through the same getEnumLabel chain.
        // Tenants may register custom labels via seedDefaults.json; when
        // absent, prettifyEnum returns a word-cased form ("access_gear"
        // → "Access Gear").
        label: getEnumLabel('resource_category', key),
        isCustom: true,
      });
    }

    return [...builtIn, ...customs];
  }, [items, getEnumLabel]);

  return { categories, loading, error, refetch };
}

export default useResourceCategories;
